"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.geminiProvider = void 0;
const __1 = require("../..");
const json_1 = require("../../../core/utils/json");
const media_1 = require("../../../core/normalize/message-parts/media");
const reasoning_1 = require("../../../core/normalize/message-parts/reasoning");
const tool_calls_1 = require("../../../core/normalize/message-parts/tool-calls");
/**
 * Gemini / Vertex convention. Gemini parts are keyed unions without a `type`
 * discriminator (functionCall, inlineData, fileData, executableCode,
 * codeExecutionResult, bare thought text) — recognized only when `type` is
 * absent. This module also owns the `contents`/`system_instruction`/
 * `candidates` container discovery and `config.tools` definitions.
 */
// Gemini `finishReason` vocabulary (reported uppercase; lookups are
// lowercased) -> the canonical FinishReason set.
const GEMINI_FINISH_REASON_TYPE_BY_RAW = {
    stop: "stop",
    max_tokens: "length",
    safety: "content-filter",
    recitation: "content-filter",
    blocklist: "content-filter",
    prohibited_content: "content-filter",
    spii: "content-filter",
    malformed_function_call: "error",
    other: "other",
};
/**
 * Keyed-union parts without a `type` discriminator: Gemini's keyed parts in
 * both SDK casings, plus the legacy function_call/function_response wrappers.
 */
function normalizeGeminiPart(value, _context) {
    if (typeof value.type === "string")
        return __1.unmatched;
    const functionCall = (0, json_1.asRecord)(value.function_call) ?? (0, json_1.asRecord)(value.functionCall);
    if (functionCall) {
        // Gemini functionCall: { name, args } (id sometimes on the wrapper).
        const part = (0, tool_calls_1.toolCallPart)({
            toolCallId: functionCall.id ?? value.id,
            toolName: functionCall.name,
            input: functionCall.args ?? functionCall.arguments,
            toolType: "functionCall",
        });
        return part ? (0, __1.claimed)(part) : __1.unmatched;
    }
    const functionResponse = (0, json_1.asRecord)(value.function_response) ?? (0, json_1.asRecord)(value.functionResponse);
    if (functionResponse) {
        // Gemini functionResponse: { name, response } — the name doubles as the
        // call id fallback, not the tool name (parity with the original join).
        return (0, __1.claimed)((0, json_1.compact)({
            type: "tool-result",
            toolCallId: (0, json_1.nullableString)(functionResponse.id ?? functionResponse.name ?? value.id),
            output: { type: "json", value: (0, json_1.toJsonValue)(functionResponse.response) },
        }));
    }
    const inlineData = (0, json_1.asRecord)(value.inline_data) ?? (0, json_1.asRecord)(value.inlineData);
    if (inlineData) {
        const data = (0, json_1.optionalString)(inlineData.data);
        if (data) {
            const reference = (0, media_1.parseMediaReference)(data);
            if (reference)
                return (0, __1.claimed)((0, media_1.filePartFromMediaReference)(reference));
            return (0, __1.claimed)((0, json_1.compact)({
                type: "file",
                mediaType: (0, json_1.optionalString)(inlineData.mime_type ?? inlineData.mimeType),
                content: { kind: "base64", data },
            }));
        }
    }
    // Record shape only: OpenAI's `file_data` is a base64 string and belongs
    // to the typed file/input_file cases.
    const geminiFileData = (0, json_1.asRecord)(value.file_data) ?? (0, json_1.asRecord)(value.fileData);
    if (geminiFileData) {
        const fileUri = (0, json_1.optionalString)(geminiFileData.file_uri ?? geminiFileData.fileUri);
        if (fileUri) {
            return (0, __1.claimed)((0, media_1.filePartFromUrl)(fileUri, {
                mediaType: (0, json_1.optionalString)(geminiFileData.mime_type ?? geminiFileData.mimeType),
            }));
        }
    }
    const executableCode = (0, json_1.asRecord)(value.executable_code) ?? (0, json_1.asRecord)(value.executableCode);
    if (executableCode) {
        return (0, __1.claimed)({
            type: "tool-call",
            toolCallId: null,
            toolName: "code_execution",
            input: (0, json_1.toJsonValue)(executableCode),
            toolType: "executable_code",
            providerExecuted: true,
        });
    }
    const codeExecutionResult = (0, json_1.asRecord)(value.code_execution_result) ??
        (0, json_1.asRecord)(value.codeExecutionResult);
    if (codeExecutionResult) {
        const outcome = (0, json_1.optionalString)(codeExecutionResult.outcome);
        return (0, __1.claimed)((0, json_1.compact)({
            type: "tool-result",
            toolCallId: null,
            toolName: "code_execution",
            output: { type: "json", value: (0, json_1.toJsonValue)(codeExecutionResult) },
            isError: outcome && outcome !== "OUTCOME_OK" ? true : undefined,
        }));
    }
    // Gemini text/thought parts: a bare `text` field, optionally flagged as
    // thought with a signature sibling.
    if (typeof value.text === "string") {
        const signature = (0, json_1.optionalString)(value.thoughtSignature ?? value.thought_signature);
        return (0, __1.claimed)(value.thought === true || signature
            ? (0, reasoning_1.reasoningPart)(value.text, signature)
            : { type: "text", text: value.text });
    }
    return __1.unmatched;
}
/**
 * Gemini requests declare tools under `config.tools`: named functions in
 * `function_declarations` groups (either casing), everything else being
 * provider built-ins (googleSearch, codeExecution, ...) that declare no
 * name. Discovery only — the core normalizes and merges the sources.
 */
function geminiToolDefinitionSources(carrier) {
    if (!carrier.root)
        return [];
    const tools = (0, json_1.parseArray)((0, json_1.asRecord)(carrier.root.config)?.tools);
    if (!tools)
        return [];
    (0, json_1.recordKeyAsParsed)(carrier.root, "config", "tools");
    return tools.map((tool, index) => {
        const toolGroup = (0, json_1.asRecord)(tool);
        const declarations = (0, json_1.parseArray)(toolGroup?.function_declarations) ??
            (0, json_1.parseArray)(toolGroup?.functionDeclarations);
        if (declarations) {
            return {
                sourceKey: `config.tools[${index}].function_declarations`,
                value: declarations,
                options: { allowToolMap: true },
            };
        }
        return {
            sourceKey: `config.tools[${index}]`,
            value: tool,
            options: { allowProviderToolWithoutName: true, allowToolMap: true },
        };
    });
}
/**
 * Gemini's request container: `contents` (+ `config.system_instruction`)
 * on input, `candidates` (each with its own finish reason) on output.
 */
function geminiSystemMessage(root, kind) {
    if (kind !== "input")
        return undefined;
    const systemInstruction = (0, json_1.recordKeyAsParsed)(root, "systemInstruction") ??
        (0, json_1.recordKeyAsParsed)(root, "system_instruction") ??
        (0, json_1.recordKeyAsParsed)(root, "config", "system_instruction") ??
        (0, json_1.recordKeyAsParsed)(root, "config", "systemInstruction");
    if (!systemInstruction)
        return undefined;
    return {
        kind: "single",
        value: systemInstruction,
        fallbackRole: "user",
        roleOverride: "system",
    };
}
function geminiMessages(root, kind) {
    const sources = [];
    if (kind === "input") {
        if ("new_message" in root) {
            sources.push({
                kind: "single",
                value: (0, json_1.recordKeyAsParsed)(root, "new_message"),
                fallbackRole: "user",
            });
            return sources;
        }
        if ("contents" in root) {
            const rawContents = (0, json_1.recordKeyAsParsed)(root, "contents");
            const contents = Array.isArray(rawContents) ? rawContents : [rawContents];
            sources.push({
                kind: "sequence",
                values: contents,
                fallbackRole: "user",
            });
        }
    }
    if (kind === "output") {
        const candidates = (0, json_1.parseArray)(root.candidates);
        if (candidates) {
            for (const candidate of candidates) {
                const candidateRecord = (0, json_1.asRecord)(candidate);
                sources.push({
                    kind: "single",
                    value: candidateRecord?.content,
                    fallbackRole: "assistant",
                    // Gemini reports the finish reason on the candidate, not the content.
                    finishReasonCarrier: candidateRecord,
                });
            }
        }
    }
    return sources;
}
exports.geminiProvider = {
    name: "gemini",
    finishReasonTypeByRaw: GEMINI_FINISH_REASON_TYPE_BY_RAW,
    roleByRawRole: { model: "assistant" },
    // Gemini contents carry their blocks in a `parts` array.
    messageLikeKeys: new Set(["parts"]),
    tryNormalizeUntypedPart: normalizeGeminiPart,
    claimMessages: geminiMessages,
    getSystemMessage: geminiSystemMessage,
    collectToolDefinitionSources: geminiToolDefinitionSources,
};
